import React from 'react';
import type { ProviderDetail } from '../../../../../js/api';
import { formatAdminRatePercent } from '../../../utils/utils';
import { formatMarketplaceFee } from './ProviderCard';
import {
  EMPTY_VALUE,
  formatHourlyRate,
  formatProviderDate,
  labelFor,
  labelsFor,
  ProviderDrawerMaps,
  textOrDash,
} from './providerDisplay';
import { FieldSection, FieldSpec, TooltippedCodes } from './ProviderDrawerPrimitives';

const uniqueCodes = (codes: Array<string | null | undefined>): string[] =>
  Array.from(new Set(codes.filter((code): code is string => Boolean(code)))).sort();

/** Code on the first line, its CodeDefinition title muted underneath (work / travel type). */
const CodeWithTitle: React.FC<{
  code: string | null | undefined;
  map?: Record<string, string>;
}> = ({ code, map }) => {
  if (!code) return <>{EMPTY_VALUE}</>;
  const title = map?.[code];
  return (
    <span className="tw:flex tw:flex-col">
      <span>{code}</span>
      {title && title !== code && <span className="tw:text-xs tw:text-zinc-500">{title}</span>}
    </span>
  );
};

const StateList: React.FC<{ states: readonly string[]; map?: Record<string, string> }> = ({
  states,
  map,
}) => (states.length ? <>{labelsFor(states, map)}</> : <>None listed</>);

/**
 * The information the legacy Bootstrap profile header showed above the tabs:
 * certifications, highlights, licensed states, work preferences and rates.
 */
export function ProviderProfileSummary({
  provider,
  maps,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  const boardTypes = uniqueCodes(provider.board_certifications.map((row) => row.type));
  const lifeSupportTypes = uniqueCodes(provider.certifications.map((row) => row.type));

  const certificationFields: FieldSpec[] = [
    {
      label: 'Board Certifications',
      value: <TooltippedCodes codes={boardTypes} map={maps.BoardCertificationType} />,
    },
    {
      label: 'Life Support',
      value: <TooltippedCodes codes={lifeSupportTypes} map={maps.CertificationType} />,
    },
  ];

  const licenseFields: FieldSpec[] = [
    {
      label: 'Medical States',
      value: (
        <span className="tw:flex tw:flex-col">
          <StateList states={provider.medical_licensed_states} map={maps.LicenseStateChoices} />
          {provider.has_imlc && (
            <span className="tw:text-xs tw:text-brand-green-0">
              Compact License (IMLC)
              {provider.imlc_expiration_date &&
                ` · expires ${formatProviderDate(provider.imlc_expiration_date)}`}
            </span>
          )}
        </span>
      ),
    },
    {
      label: 'DEA States',
      value: <StateList states={provider.dea_licensed_states} map={maps.LicenseStateChoices} />,
    },
  ];

  const workFields: FieldSpec[] = [
    {
      label: 'Target Shifts',
      value: <CodeWithTitle code={provider.work_type} map={maps.WorkType} />,
    },
    {
      label: 'Travel Area',
      value: <CodeWithTitle code={provider.travel_type} map={maps.TravelType} />,
    },
    { label: 'Scheduling', value: labelFor(provider.schedule_type, maps.ScheduleType) },
    { label: 'Shift Limit', value: textOrDash(provider.shift_limit) },
  ];

  const rateFields: FieldSpec[] = [
    { label: 'Day', value: formatHourlyRate(provider.shift_rate_day) },
    { label: 'Night', value: formatHourlyRate(provider.shift_rate_night) },
    { label: '24 Hour', value: formatHourlyRate(provider.shift_rate_24h) },
    { label: 'Call', value: formatHourlyRate(provider.shift_rate_call) },
    { label: 'Admin Rate', value: formatAdminRatePercent(provider.admin_rate) },
    { label: 'Marketplace Fee', value: formatMarketplaceFee(provider.marketplace_hourly_fee) },
  ];

  const minimumFields: FieldSpec[] = [
    { label: 'Day', value: formatHourlyRate(provider.min_rate_day) },
    { label: 'Night', value: formatHourlyRate(provider.min_rate_night) },
    { label: '24 Hour', value: formatHourlyRate(provider.min_rate_24h) },
    { label: 'Call', value: formatHourlyRate(provider.min_rate_call) },
  ];

  return (
    <div
      className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-3 tw:gap-y-4 tw:mt-4"
      data-testid="provider-profile-summary"
    >
      <FieldSection title="Certifications" fields={certificationFields} />
      <FieldSection title="Licensed States" fields={licenseFields} />
      <FieldSection
        title="Professional Highlights"
        className="tw:md:col-span-2"
        fields={[
          {
            label: 'Bio',
            value: provider.professional_highlights || EMPTY_VALUE,
            whitespacePreLine: true,
          },
        ]}
      />
      <FieldSection title="Work Preferences" fields={workFields} />
      <div className="tw:flex tw:flex-col tw:gap-4">
        <FieldSection title="Provider Rates" fields={rateFields} />
        <FieldSection title="Minimum Rates" fields={minimumFields} />
      </div>
    </div>
  );
}
